/**
 * The boot screen's model: progress and status derived from real work only
 * (CLAUDE.md rule 7). It listens to engine module events and is told about the
 * startup level's byte stream. There is no scripted timeline.
 */
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import type { Emitter, Unsubscribe } from "../../core/Emitter";
import type { EngineEvents } from "../../core/Engine";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";

export type BootPhase = "booting" | "streaming" | "ready" | "failed";

export interface BootState {
  readonly phase: BootPhase;
  /** 0..1, per `computeBootProgress`. */
  readonly progress: number;
  /** What's happening now, e.g. "Streaming /Roles/Riot_Games.level". */
  readonly status: string;
  readonly error: string | null;
}

export interface BootProgressInput {
  readonly modulesDone: number;
  readonly modulesTotal: number;
  readonly bytesLoaded: number;
  readonly bytesTotal: number | null;
}

/** Share of the bar owned by module initialization; the rest is startup-level bytes (A-23). */
const MODULE_WEIGHT = 0.3;

export function computeBootProgress(input: BootProgressInput): number {
  const modules = fraction(input.modulesDone, input.modulesTotal);
  const bytes = input.bytesTotal === null ? 0 : fraction(input.bytesLoaded, input.bytesTotal);
  return MODULE_WEIGHT * modules + (1 - MODULE_WEIGHT) * bytes;
}

export interface DismissInput {
  readonly ready: boolean;
  readonly elapsedMs: number;
  readonly minDisplayMs: number;
  /** Esc cancels the minimum display time; it can't skip real work (A-23). */
  readonly skipped: boolean;
}

export function shouldDismissBoot(input: DismissInput): boolean {
  return input.ready && (input.skipped || input.elapsedMs >= input.minDisplayMs);
}

export class BootMonitor implements ReadableStore<BootState> {
  private readonly store: VersionedStore<BootState>;
  private readonly counters = {
    modulesDone: 0,
    modulesTotal: 0,
    bytesLoaded: 0,
    bytesTotal: null as number | null,
  };

  constructor(scheduler: ChangeScheduler) {
    this.store = new VersionedStore<BootState>(scheduler, {
      phase: "booting",
      progress: 0,
      status: "Starting",
      error: null,
    });
  }

  readonly subscribe = (listener: () => void): Unsubscribe => this.store.subscribe(listener);
  readonly getSnapshot = (): BootState => this.store.getSnapshot();

  /** Follows an engine's module events. Returns a function that detaches. */
  attach(events: Emitter<EngineEvents>): Unsubscribe {
    const offs = [
      events.on("boot:start", (e) => {
        this.counters.modulesTotal = e.modules.length;
        this.update({});
      }),
      events.on("module:start", (e) => {
        this.update({ status: `Initializing ${e.name}` });
      }),
      events.on("module:done", () => {
        this.counters.modulesDone++;
        this.update({});
      }),
    ];
    return () => {
      offs.forEach((off) => {
        off();
      });
    };
  }

  beginStreaming(assetPath: string, totalBytes: number | null): void {
    this.counters.bytesLoaded = 0;
    this.counters.bytesTotal = totalBytes;
    this.update({ phase: "streaming", status: `Streaming ${assetPath}` });
  }

  streamProgress(loaded: number, total: number | null): void {
    this.counters.bytesLoaded = loaded;
    if (total !== null) this.counters.bytesTotal = total;
    this.update({});
  }

  markReady(): void {
    this.store.publish({
      ...this.store.getSnapshot(),
      phase: "ready",
      progress: 1,
      status: "Ready",
    });
  }

  markFailed(error: string): void {
    this.store.publish({
      ...this.store.getSnapshot(),
      phase: "failed",
      status: "Boot failed",
      error,
    });
  }

  private update(patch: Partial<BootState>): void {
    const current = this.store.getSnapshot();
    if (current.phase === "ready" || current.phase === "failed") return;
    this.store.publish({ ...current, ...patch, progress: computeBootProgress(this.counters) });
  }
}

function fraction(done: number, total: number): number {
  return total > 0 ? Math.min(1, done / total) : 0;
}
